/**
 * Render-time listing resolution.
 *
 * Blocks persist a listing *reference* (an id) and nothing else — never the
 * resolved address/price/photos. Those live here, in memory, and are fetched on
 * demand. That keeps a saved design from carrying a stale copy of listing data
 * that has since changed, sold, or been deleted.
 *
 * Block `render` is synchronous, so it cannot await a lookup. The contract is:
 *
 *   1. render calls `getListing(id)` — a synchronous cache read
 *   2. a miss returns `undefined` and schedules a fetch as a side effect
 *   3. render draws the loading state for this pass
 *   4. when the fetch lands, subscribers fire and the canvas re-renders
 *
 * Core stays transport-agnostic: it holds no endpoint and no HTTP client. The
 * extensions package registers a resolver at boot via `setListingResolver`.
 */

export interface ResolvedListing {
	id: string;
	[key: string]: any;
}

/** Resolve ids to listings. Omit an id from the result to mark it unavailable. */
export type ListingResolver = (
	_ids: string[],
) => Promise<Record<string, ResolvedListing>>;

const cache = new Map<string, ResolvedListing>();
/** Ids currently being fetched — render draws these as loading. */
const inFlight = new Set<string>();
/** Ids a resolver has already reported as unavailable. Prevents refetch loops. */
const unavailable = new Set<string>();
const subscribers = new Set<() => void>();

let resolver: ListingResolver | null = null;

/**
 * Ids waiting for the coalesced flush in `requestListings`. Declared up here,
 * not beside the function that uses them: module-level `let` is in the temporal
 * dead zone until this module finishes evaluating, and this package has circular
 * imports — a binding referenced before its declaration line has thrown
 * "is not defined" here before and taken the canvas down with it.
 */
let pendingIds: string[] = [];
let flushScheduled = false;

function notify(): void {
	subscribers.forEach(callback => {
		try {
			callback();
		} catch (error) {
			console.error('[listingCache] subscriber threw:', error);
		}
	});
}

/** Install the host/package resolver. Passing null disables lookups entirely. */
export function setListingResolver(next: ListingResolver | null): void {
	resolver = next;
}

/**
 * Seed listings the picker already has in hand, so selecting one paints
 * immediately instead of round-tripping for data the panel just fetched.
 */
export function primeListings(
	listings: Array<ResolvedListing | null | undefined>,
): void {
	let changed = false;

	listings.forEach(listing => {
		if (!listing?.id) return;
		cache.set(listing.id, listing);
		unavailable.delete(listing.id);
		inFlight.delete(listing.id);
		changed = true;
	});

	if (changed) notify();
}

/** Synchronous cache read. Does not fetch — see `getListing`. */
export function peekListing(id?: string | null): ResolvedListing | undefined {
	if (!id) return undefined;
	return cache.get(id);
}

export function isListingLoading(id?: string | null): boolean {
	return Boolean(id) && inFlight.has(id as string);
}

export function isListingUnavailable(id?: string | null): boolean {
	return Boolean(id) && unavailable.has(id as string);
}

/**
 * Fetch any ids that are not cached, in flight, or already known unavailable.
 * Fire-and-forget: callers re-read through the cache once subscribers fire.
 */
export function requestListings(ids: Array<string | null | undefined>): void {
	if (!resolver) return;

	const wanted = ids.filter(
		(id): id is string =>
			Boolean(id) &&
			!cache.has(id as string) &&
			!inFlight.has(id as string) &&
			!unavailable.has(id as string),
	);

	if (!wanted.length) return;

	wanted.forEach(id => inFlight.add(id));
	notify(); // let the canvas paint its loading state

	// Coalesce everything asked for in this tick into ONE resolver call.
	//
	// Every bound block calls getListing() during its own render, so a design
	// with N attached listings produced N separate calls here, each becoming its
	// own HTTP round trip. The resolver has always accepted an array and joins
	// the ml_numbers into a single query — it simply never received more than one
	// id, because nothing collected them first. On a slow compose pass (~12s per
	// request once doQueryFields runs) that difference is the whole load time.
	//
	// A microtask is enough: React renders the whole tree synchronously, so every
	// block's getListing() has run by the time this drains. Nothing waits longer
	// than it already did — the flush happens before the network could have
	// answered anyway.
	//
	// The inFlight marking above stays synchronous on purpose. It is what stops a
	// second caller in the same tick from queueing a duplicate id, and what the
	// loading states read.
	pendingIds.push(...wanted);
	if (flushScheduled) return;
	flushScheduled = true;
	Promise.resolve().then(flushPendingListings);
}


function flushPendingListings(): void {
	flushScheduled = false;

	const wanted = Array.from(new Set(pendingIds));
	pendingIds = [];

	if (!resolver || !wanted.length) return;

	resolver(wanted)
		.then(resolved => {
			wanted.forEach(id => {
				const listing = resolved?.[id];
				if (listing) {
					cache.set(id, listing);
				} else {
					// Every missing-data cause lands here — deleted, status change,
					// type mismatch, or simply absent from the response. Callers render
					// the same fallback for all of them.
					unavailable.add(id);
				}
			});
		})
		.catch(error => {
			// A transient failure is a missing value for this pass, not a retry loop.
			// The id stays out of `unavailable` so a later render can try again.
			console.error('[listingCache] resolve failed:', error);
		})
		.finally(() => {
			wanted.forEach(id => inFlight.delete(id));
			notify();
		});
}

/**
 * The call render should use: returns the listing if cached, otherwise starts a
 * fetch and returns undefined so this pass draws the loading state.
 */
export function getListing(id?: string | null): ResolvedListing | undefined {
	if (!id) return undefined;

	const cached = cache.get(id);
	if (cached) return cached;

	requestListings([id]);
	return undefined;
}

/** Re-render hook for the canvas. Returns an unsubscribe function. */
export function subscribeListings(callback: () => void): () => void {
	subscribers.add(callback);
	return () => {
		subscribers.delete(callback);
	};
}

/* ── Criteria bindings ──────────────────────────────────────────────────────
 * A criteria block is bound to a saved *query* rather than to specific ids, so
 * it resolves through its own cache keyed by that query. Same contract as
 * above: a sync read, a scheduled fetch on a miss, subscribers on arrival.
 */

export type CriteriaResolver = (
	_config: Record<string, any>,
) => Promise<ResolvedListing[]>;

const criteriaCache = new Map<string, ResolvedListing[]>();
const criteriaInFlight = new Set<string>();
let criteriaResolver: CriteriaResolver | null = null;

/** Stable key for a criteria config — key order must not change the identity. */
function criteriaKey(config: Record<string, any>): string {
	try {
		return JSON.stringify(
			Object.keys(config)
				.sort()
				.reduce<Record<string, any>>((acc, key) => {
					acc[key] = config[key];
					return acc;
				}, {}),
		);
	} catch {
		return '';
	}
}

export function setCriteriaResolver(next: CriteriaResolver | null): void {
	criteriaResolver = next;
}

export function isCriteriaLoading(config?: Record<string, any> | null): boolean {
	if (!config) return false;
	return criteriaInFlight.has(criteriaKey(config));
}

/**
 * Listings matching a criteria binding. Returns undefined on a miss and starts
 * the search; the canvas re-renders when it lands.
 */
export function getCriteriaListings(
	config?: Record<string, any> | null,
): ResolvedListing[] | undefined {
	if (!config || !criteriaResolver) return undefined;

	const key = criteriaKey(config);
	if (!key) return undefined;

	const cached = criteriaCache.get(key);
	if (cached) return cached;
	if (criteriaInFlight.has(key)) return undefined;

	criteriaInFlight.add(key);
	notify();

	criteriaResolver(config)
		.then(listings => {
			criteriaCache.set(key, listings || []);
			// Individual listings are useful to the id cache too.
			primeListings(listings || []);
		})
		.catch(error => {
			console.error('[listingCache] criteria resolve failed:', error);
		})
		.finally(() => {
			criteriaInFlight.delete(key);
			notify();
		});

	return undefined;
}

/** Drop everything — used when switching accounts or acting-as identity. */
export function clearListingCache(): void {
	cache.clear();
	inFlight.clear();
	unavailable.clear();
	criteriaCache.clear();
	criteriaInFlight.clear();
	notify();
}
